import type { AxiosError, AxiosResponse } from 'axios';

export type ApiErrorCode =
  | 'NETWORK'
  | 'TIMEOUT'
  | 'CANCELED'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'VALIDATION'
  | 'CONFLICT'
  | 'RATE_LIMITED'
  | 'SERVER'
  | 'UNKNOWN';

interface NestValidationPayload {
  statusCode?: number;
  message?: string | string[];
  error?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  readonly payload: unknown;
  readonly fields?: Record<string, string>;

  constructor(opts: {
    message: string;
    status: number;
    code: ApiErrorCode;
    payload?: unknown;
    fields?: Record<string, string>;
  }) {
    super(opts.message);
    this.name = 'ApiError';
    this.status = opts.status;
    this.code = opts.code;
    this.payload = opts.payload;
    this.fields = opts.fields;
  }

  static fromAxios(err: AxiosError): ApiError {
    if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError') {
      return new ApiError({
        message: 'Request canceled',
        status: 0,
        code: 'CANCELED',
        payload: err.message,
      });
    }
    if (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT') {
      return new ApiError({
        message: 'Request timed out',
        status: 0,
        code: 'TIMEOUT',
        payload: err.message,
      });
    }
    if (!err.response) {
      return new ApiError({
        message: err.message || 'Network error',
        status: 0,
        code: 'NETWORK',
        payload: err.message,
      });
    }
    return ApiError.fromResponse(err.response);
  }

  static fromResponse(res: AxiosResponse): ApiError {
    const status = res.status;
    const payload = res.data as NestValidationPayload | undefined;
    const rawMessage = payload?.message;
    const messages = Array.isArray(rawMessage) ? rawMessage : rawMessage ? [rawMessage] : [];
    const message = messages[0] ?? payload?.error ?? `Request failed (${status})`;

    return new ApiError({
      message,
      status,
      code: codeFromStatus(status),
      payload,
      fields: extractFieldErrors(messages),
    });
  }
}

function codeFromStatus(status: number): ApiErrorCode {
  if (status === 401) return 'UNAUTHORIZED';
  if (status === 403) return 'FORBIDDEN';
  if (status === 404) return 'NOT_FOUND';
  if (status === 409) return 'CONFLICT';
  if (status === 422 || status === 400) return 'VALIDATION';
  if (status === 429) return 'RATE_LIMITED';
  if (status >= 500) return 'SERVER';
  return 'UNKNOWN';
}

/**
 * NestJS class-validator returns messages like "email must be an email".
 * Map back to { field: message } when the first token matches a property.
 */
function extractFieldErrors(messages: string[]): Record<string, string> | undefined {
  if (messages.length === 0) return undefined;
  const fields: Record<string, string> = {};
  for (const msg of messages) {
    const match = /^([a-zA-Z][a-zA-Z0-9_]*)\s+(.*)$/.exec(msg);
    if (match) {
      const [, field, rest] = match;
      if (!fields[field]) fields[field] = rest;
    }
  }
  return Object.keys(fields).length > 0 ? fields : undefined;
}
